(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const hasResizeObserver = typeof window.ResizeObserver === 'function';

  const syncRail = (rail, previous, next) => {
    const max = Math.max(rail.scrollWidth - rail.clientWidth, 0);
    previous?.toggleAttribute('disabled', rail.scrollLeft <= 2);
    next?.toggleAttribute('disabled', rail.scrollLeft >= max - 2);
  };

  document.querySelectorAll('[data-fizo-about]').forEach((root) => {
    if (root.dataset.fizoAboutReady === 'true') return;
    root.dataset.fizoAboutReady = 'true';

    const abortController = new AbortController();
    const {signal} = abortController;
    const manifesto = root.querySelector('[data-fizo-about-manifesto]');
    const manifestoTrack = root.querySelector('.fizo-about__manifesto-track');
    const manifestoCopy = root.querySelector('.fizo-about__manifesto-copy');
    const manifestoMedia = root.querySelector('.fizo-about__manifesto-stage .fizo-about__background-media');
    const manifestoLines = [...root.querySelectorAll('[data-fizo-manifesto-line]')];
    let manifestoVisualLines = [];
    let manifestoFrame = 0;
    let manifestoTravel = 0;

    const restoreManifestoLines = () => {
      manifestoLines.forEach((line) => {
        if (line.dataset.fizoManifestoCopy) line.textContent = line.dataset.fizoManifestoCopy;
      });
      manifestoVisualLines = [];
      manifestoTravel = 0;
      manifestoCopy?.style.removeProperty('transform');
      manifesto?.style.setProperty('--fizo-manifesto-scroll-distance', desktopQuery.matches ? '260svh' : '160svh');
    };

    const measureManifestoTravel = () => {
      if (!manifesto || !manifestoTrack || !manifestoCopy || !manifestoVisualLines.length || reducedMotion) return;

      manifestoCopy.style.removeProperty('transform');
      const finalLine = manifestoVisualLines.at(-1);
      const finalBounds = finalLine.getBoundingClientRect();
      const copyBounds = manifestoCopy.getBoundingClientRect();
      const target = window.innerHeight * .5;
      const copyTopWithinManifesto = Number.parseFloat(getComputedStyle(manifestoCopy).top) || 0;
      const finalCenterWithinCopy = finalBounds.top - copyBounds.top + finalBounds.height / 2;

      // Sticky positioning changes the section's viewport coordinates while scrolling.
      // Use the CSS copy origin so this calculation is stable after a refresh as well.
      manifestoTravel = Math.max(copyTopWithinManifesto + finalCenterWithinCopy - target, 0);
      manifesto.style.setProperty('--fizo-manifesto-scroll-distance', desktopQuery.matches ? '260svh' : '160svh');
    };

    const getManifestoVisibleMediaBounds = () => {
      if (!manifestoMedia) return null;

      const bounds = manifestoMedia.getBoundingClientRect();
      const intrinsicWidth = manifestoMedia.videoWidth || manifestoMedia.naturalWidth;
      const intrinsicHeight = manifestoMedia.videoHeight || manifestoMedia.naturalHeight;
      if (!intrinsicWidth || !intrinsicHeight) return bounds;

      const intrinsicRatio = intrinsicWidth / intrinsicHeight;
      const containerRatio = bounds.width / bounds.height;
      if (containerRatio > intrinsicRatio) {
        const width = bounds.height * intrinsicRatio;
        const offset = (bounds.width - width) / 2;
        return {top: bounds.top, right: bounds.right - offset, bottom: bounds.bottom, left: bounds.left + offset};
      }

      const height = bounds.width / intrinsicRatio;
      const offset = (bounds.height - height) / 2;
      return {top: bounds.top + offset, right: bounds.right, bottom: bounds.bottom - offset, left: bounds.left};
    };

    const buildManifestoLines = () => {
      if (!manifesto || !manifestoLines.length) return;
      if (reducedMotion) {
        restoreManifestoLines();
        return;
      }

      manifestoLines.forEach((line) => {
        const copy = line.dataset.fizoManifestoCopy || line.textContent.trim();
        line.dataset.fizoManifestoCopy = copy;
        line.replaceChildren();

        const words = copy.split(/\s+/).filter(Boolean);
        words.forEach((word, index) => {
          const wordNode = document.createElement('span');
          wordNode.className = 'fizo-about__manifesto-word';
          wordNode.textContent = `${word}${index === words.length - 1 ? '' : ' '}`;
          line.append(wordNode);
        });

        const groups = [];
        [...line.children].forEach((wordNode) => {
          const lastGroup = groups.at(-1);
          if (!lastGroup || Math.abs(lastGroup.top - wordNode.offsetTop) > 1) {
            groups.push({top: wordNode.offsetTop, copy: wordNode.textContent});
          } else {
            lastGroup.copy += wordNode.textContent;
          }
        });

        line.replaceChildren();
        groups.forEach((group) => {
          const visualLine = document.createElement('span');
          visualLine.className = 'fizo-about__manifesto-visual-line';
          visualLine.dataset.fizoManifestoVisualLine = '';
          visualLine.textContent = group.copy.trim();
          line.append(visualLine);
        });
      });

      manifestoVisualLines = [...root.querySelectorAll('[data-fizo-manifesto-visual-line]')];
      measureManifestoTravel();
    };

    const updateManifesto = () => {
      manifestoFrame = 0;
      if (!manifesto || !manifestoCopy || !manifestoVisualLines.length || reducedMotion) return;

      const manifestoBounds = manifesto.getBoundingClientRect();
      const scrollDistance = Math.max(manifesto.offsetHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(-manifestoBounds.top / scrollDistance, 0), 1);
      const travel = -manifestoTravel * progress;
      manifestoCopy.style.transform = `translate3d(0, ${travel}px, 0)`;

      const center = window.innerHeight * .5;
      const visibleMediaBounds = desktopQuery.matches ? null : getManifestoVisibleMediaBounds();
      let activeIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      manifestoVisualLines.forEach((line, index) => {
        const bounds = line.getBoundingClientRect();
        const isOutsideMobileMedia = visibleMediaBounds
          && (bounds.top < visibleMediaBounds.top || bounds.bottom > visibleMediaBounds.bottom);
        line.classList.toggle('is-outside-media', Boolean(isOutsideMobileMedia));
        const distance = Math.abs(bounds.top + bounds.height / 2 - center);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          activeIndex = index;
        }
      });
      manifestoVisualLines.forEach((line, index) => line.classList.toggle('is-active', index === activeIndex));
    };

    const scheduleManifesto = () => {
      if (!manifestoFrame) manifestoFrame = window.requestAnimationFrame(updateManifesto);
    };

    const rebuildManifesto = () => {
      buildManifestoLines();
      measureManifestoTravel();
      scheduleManifesto();
    };

    window.addEventListener('scroll', scheduleManifesto, {signal, passive: true});
    window.addEventListener('resize', rebuildManifesto, {signal, passive: true});
    desktopQuery.addEventListener('change', rebuildManifesto, {signal});
    manifestoMedia?.addEventListener('loadedmetadata', scheduleManifesto, {signal});
    manifestoMedia?.addEventListener('load', scheduleManifesto, {signal});
    buildManifestoLines();
    document.fonts?.ready?.then(rebuildManifesto);
    updateManifesto();

    root.querySelectorAll('[data-fizo-about-rail]').forEach((rail) => {
      const section = rail.closest('.fizo-about__carousel-section');
      const previous = section?.querySelector('[data-fizo-about-rail-prev]');
      const next = section?.querySelector('[data-fizo-about-rail-next]');
      if (!section || !previous || !next) return;

      const move = (direction) => {
        rail.scrollBy({left: direction * rail.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth'});
      };
      previous.addEventListener('click', () => move(-1), {signal});
      next.addEventListener('click', () => move(1), {signal});
      rail.addEventListener('scroll', () => syncRail(rail, previous, next), {signal, passive: true});
      if (hasResizeObserver) {
        new ResizeObserver(() => syncRail(rail, previous, next)).observe(rail);
      } else {
        window.addEventListener('resize', () => syncRail(rail, previous, next), {signal, passive: true});
      }
      syncRail(rail, previous, next);
    });

    const mobileFocusItems = [
      ...root.querySelectorAll('[data-fizo-about-value], [data-fizo-about-story]'),
    ];
    let mobileFocusFrame = 0;
    const updateMobileFocus = () => {
      mobileFocusFrame = 0;
      if (desktopQuery.matches) {
        mobileFocusItems.forEach((item) => item.classList.remove('is-mobile-active'));
        return;
      }

      const center = window.innerHeight * .5;
      let activeItem = null;
      let closestDistance = Number.POSITIVE_INFINITY;
      mobileFocusItems.forEach((item) => {
        const bounds = item.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
        const distance = Math.abs(bounds.top + bounds.height / 2 - center);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeItem = item;
        }
      });
      mobileFocusItems.forEach((item) => item.classList.toggle('is-mobile-active', item === activeItem));
    };
    const scheduleMobileFocus = () => {
      if (!mobileFocusFrame) mobileFocusFrame = window.requestAnimationFrame(updateMobileFocus);
    };
    window.addEventListener('scroll', scheduleMobileFocus, {signal, passive: true});
    window.addEventListener('resize', scheduleMobileFocus, {signal, passive: true});
    desktopQuery.addEventListener('change', scheduleMobileFocus, {signal});
    updateMobileFocus();

    const steps = [...root.querySelectorAll('[data-fizo-about-process-step]')];
    const processGrid = root.querySelector('[data-fizo-about-process-grid]');
    if (steps.length && !reducedMotion) {
      // The design file shows card two flipped only as an interaction example.
      // Every card must render on its front face until the user reaches it.
      steps.forEach((step) => step.classList.remove('is-flipped'));
      let mobileProcessHasScrolled = false;

      // Let flexbox calculate the final evenly-spaced layout once, then animate
      // each card from its previous geometry with compositor-only transforms.
      const animateProcessLayout = (applyLayout) => {
        if (!processGrid || !desktopQuery.matches) {
          applyLayout();
          return;
        }

        steps.forEach((item) => item.getAnimations().forEach((animation) => animation.cancel()));
        const first = new Map(steps.map((item) => [item, item.getBoundingClientRect()]));
        applyLayout();

        steps.forEach((item) => {
          const previous = first.get(item);
          const next = item.getBoundingClientRect();
          if (!previous || !next.width || !next.height) return;

          const translateX = previous.left - next.left;
          const translateY = previous.top - next.top;
          const scaleX = previous.width / next.width;
          const scaleY = previous.height / next.height;
          if (Math.abs(translateX) < .5 && Math.abs(translateY) < .5 && Math.abs(scaleX - 1) < .001 && Math.abs(scaleY - 1) < .001) return;

          item.animate([
            { transformOrigin: 'top left', transform: `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})` },
            { transformOrigin: 'top left', transform: 'translate(0, 0) scale(1, 1)' },
          ], {
            duration: 280,
            easing: 'cubic-bezier(.2, .8, .2, 1)',
            fill: 'both',
          });
        });
      };

      // Do not rely on the browser's hover media capability. Desktop previews can
      // report a touch pointer even when a physical mouse is available.
      let activeProcessStep = null;
      const resetProcessLayout = () => {
        steps.forEach((item) => item.classList.remove('is-hovered'));
        processGrid?.style.removeProperty('--fizo-process-base-width');
        processGrid?.style.removeProperty('--fizo-process-base-height');
        processGrid?.style.removeProperty('--fizo-process-active-width');
        processGrid?.style.removeProperty('--fizo-process-active-height');
        processGrid?.style.removeProperty('--fizo-process-scale');
      };
      const showStepBack = (step) => {
        if (!desktopQuery.matches || activeProcessStep === step) return;
        animateProcessLayout(() => {
          resetProcessLayout();
          const bounds = step.getBoundingClientRect();
          const scale = 1.3675;
          processGrid?.style.setProperty('--fizo-process-base-width', `${bounds.width}px`);
          processGrid?.style.setProperty('--fizo-process-base-height', `${bounds.height}px`);
          processGrid?.style.setProperty('--fizo-process-active-width', `${bounds.width * scale}px`);
          processGrid?.style.setProperty('--fizo-process-active-height', `${bounds.height * scale}px`);
          processGrid?.style.setProperty('--fizo-process-scale', String(scale));
          step.classList.add('is-hovered');
          activeProcessStep = step;
        });
      };
      const hideStepBack = () => {
        if (!activeProcessStep) return;
        animateProcessLayout(() => {
          resetProcessLayout();
          activeProcessStep = null;
        });
      };
      steps.forEach((step) => {
        step.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch' || !desktopQuery.matches) return;
          showStepBack(step);
        }, {signal});
      });
      processGrid?.addEventListener('pointerleave', hideStepBack, {signal});

      const updateMobileProcessFlip = () => {
        if (desktopQuery.matches || !mobileProcessHasScrolled) return;
        const trigger = window.innerHeight * .2;
        let activeStep = null;
        let closestDistance = Number.POSITIVE_INFINITY;
        steps.forEach((step) => {
          const bounds = step.getBoundingClientRect();
          if (bounds.top > trigger || bounds.bottom <= trigger) return;
          const distance = Math.abs(bounds.top - trigger);
          if (distance < closestDistance) {
            closestDistance = distance;
            activeStep = step;
          }
        });
        steps.forEach((step) => step.classList.toggle('is-flipped', step === activeStep));
      };

      const handleMobileProcessScroll = () => {
        mobileProcessHasScrolled = true;
        updateMobileProcessFlip();
      };

      window.addEventListener('scroll', handleMobileProcessScroll, {signal, passive: true});
      window.addEventListener('resize', updateMobileProcessFlip, {signal, passive: true});
      desktopQuery.addEventListener('change', () => {
        mobileProcessHasScrolled = false;
        activeProcessStep = null;
        steps.forEach((step) => step.classList.remove('is-flipped', 'is-hovered'));
        processGrid?.style.removeProperty('--fizo-process-base-width');
        processGrid?.style.removeProperty('--fizo-process-base-height');
        processGrid?.style.removeProperty('--fizo-process-active-width');
        processGrid?.style.removeProperty('--fizo-process-active-height');
        processGrid?.style.removeProperty('--fizo-process-scale');
      }, {signal});
    }

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail?.sectionId === root.dataset.sectionId) abortController.abort();
    }, {signal});
  });
})();
